import { observer } from 'mobx-react-lite';
import { MainStackProps } from '../../../../types/screens';
import {
  FlatList,
  LayoutChangeEvent,
  LayoutRectangle,
  ListRenderItemInfo,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { commonStyles } from '../../../../common/styles';
import TvPressable from '../../../../components/tv_pressable';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import useGlobalStyles from '../../../../common/style_context';
import { FocusStateType, useFocusState } from '../../../../common/hooks';
import { IdNameType, VoidCb } from '../../../../types/others';
import useScale from '../../../../common/hooks/useScale';
import AppColors from '../../../../common/colors';
import Constants from '../../../../utils/constants';
import KeyWrapper from '../../../../components/key_wrapper';
import { useOnTop } from '../../../../common/hooks/useOnTop';
import AppIcon, { AppIcons } from '../../../../components/app_icon';
import { computed, reaction } from 'mobx';
import localization from '../../../../common/localization';
import _ from 'lodash';
import { MainStackParams } from '../../../../common/screen_params';
import States from '../../../../common/states';

type Props = MainStackProps<'FilterDialogTv'>;

const dialogStyles = [
  'w=600,h=100%,bg=#000000BF,pl=24',
  'fs=48,fw=700,c=white,mb=40',
];

const FiltersDialogTv = observer<Props>(({ navigation, route: { params } }) => {
  const enabled = useOnTop(navigation);

  const items = useMemo(() => States.contentState._filters[params.key], []);
  const focusState = useFocusState(items.length + 2);

  const [container, titleStyle] = useGlobalStyles(dialogStyles);
  const scales = useScale();
  const style = useMemo(
    () =>
      StyleSheet.compose(container, {
        paddingTop: Constants.tvSafeArea.top / scales[0],
        paddingBottom: Constants.tvSafeArea.bottom / scales[0],
        paddingRight: Constants.tvSafeArea.horizontal / scales[0],
      }),
    [container, scales],
  );
  const itemSize = useMemo(
    () => ((itemHeight + itemGap) / scales[0]) * scales[1],
    [scales],
  );

  const ref = useRef<FlatList>(null);
  const prev = useRef(0);
  const layout = useRef<LayoutRectangle>(undefined);

  useEffect(() => {
    return reaction(
      () => focusState.focused,
      value => {
        if (value === undefined) return;

        if (value < prev.current) {
          ref.current?.scrollToIndex({ animated: false, index: value });
          prev.current = value;
          return;
        }
        if (layout.current === undefined) return;
        const count = Math.floor(layout.current.height / itemSize);
        if (value >= prev.current + count) {
          const index = value - count + 1;
          ref.current?.scrollToIndex({ animated: false, index });
          prev.current = index;
        }
      },
    );
  }, [itemSize]);

  const up = useCallback((keyUp?: boolean) => {
    if (keyUp) return;
    focusState.decrement();
  }, []);
  const down = useCallback((keyUp?: boolean) => {
    if (keyUp) return;
    focusState.increment();
  }, []);
  const right = useCallback(
    (keyUp?: boolean) => {
      if (keyUp || focusState.focused === undefined) return;
      if (focusState.focused < items.length) focusState.focused = items.length;
    },
    [params],
  );
  const confirm = useCallback(() => {
    if (focusState.focused === undefined) return;
    if (focusState.focused === items.length) {
      const p: MainStackParams['CatalogTv'] = {};
      p[params.key] =
        params.selected !== undefined
          ? _.uniq(
              items.filter(e => params.selected!.includes(e.id)).map(e => e.id),
            )
          : undefined;
      navigation.popTo('CatalogTv', p, { merge: true });
      return;
    }

    if (focusState.focused === items.length + 1) {
      navigation.setParams({
        selected: params.mustHave ? [items[0].id] : undefined,
      });
      return;
    }

    const id = items[focusState.focused].id;
    if (params.multiple) {
      const index = params.selected?.indexOf(id) ?? -1;
      if (index >= 0) {
        const res = Array.from(params.selected!);
        res.splice(index, 1);
        navigation.setParams({
          selected: res.length > 0 ? res : undefined,
        });
      } else {
        if (params.selected)
          navigation.setParams({
            selected: [...params.selected, id],
          });
        else
          navigation.setParams({
            selected: [id],
          });
      }
    } else {
      navigation.setParams({
        selected:
          params.selected?.[0] !== id || params.mustHave ? [id] : undefined,
      });
    }
  }, [params]);
  const cancel = useCallback(() => navigation.pop(), []);

  const renderItem = useCallback(
    ({ item, index }: ListRenderItemInfo<IdNameType>) => (
      <FilterItem
        title={item.name}
        index={index}
        selected={params.selected?.includes(item.id)}
        multiple={params.multiple}
        focusState={focusState}
        onPress={confirm}
      />
    ),
    [params.selected, params.multiple, confirm],
  );
  const getItemLayout = useCallback(
    (_: any, index: number) => ({
      length: itemSize,
      offset: itemSize * index,
      index,
    }),
    [itemSize],
  );
  const keyExtractor = useCallback(
    (item: IdNameType, index: number) => `${item.id}_${index}`,
    [],
  );

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    const l = e.nativeEvent.layout;
    if (l.height === 0) return;
    layout.current = l;
  }, []);

  return (
    <KeyWrapper
      enabled={enabled}
      up={up}
      down={down}
      right={right}
      confirm={confirm}
      cancel={cancel}
      style={styles.container}>
      <TvPressable onPress={cancel} style={commonStyles.fill} />
      <View style={style}>
        <Text style={titleStyle}>{params.title}</Text>
        <View onLayout={onLayout} style={commonStyles.f1}>
          <FlatList
            data={items}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            getItemLayout={getItemLayout}
            removeClippedSubviews={false}
            scrollEnabled={false}
            showsVerticalScrollIndicator={false}
            ref={ref}
          />
        </View>
        <FilterButton
          text={localization.content.catalogApply}
          index={items.length}
          focusState={focusState}
          onPress={confirm}
        />
        <FilterButton
          text={localization.content.catalogReset}
          index={items.length + 1}
          focusState={focusState}
          onPress={confirm}
        />
      </View>
    </KeyWrapper>
  );
});
FiltersDialogTv.displayName = 'FiltersDialogTv';

const itemHeight = 65;
const itemGap = 6;

const itemStyles = [
  `fr,ac,h=${itemHeight},mb=${itemGap},pl=24`,
  `fill,bg=${AppColors.mainBlue},br=12`,
  `hide,fill,bg=${AppColors.mainBlue},br=12`,
  'f=1,fs=32,fw=500,c=white,ml=16',
];

type ItemProps = {
  title: string;
  selected?: boolean;
  multiple?: boolean;
  index: number;
  focusState: FocusStateType;
  onPress?: VoidCb;
};

const FilterItem = observer<ItemProps>(
  ({ title, selected, multiple, index, focusState, onPress }) => {
    const focused = computed(() => focusState.focused === index).get();
    const [container, focusedContainer, unfocusedContainer, textStyle] =
      useGlobalStyles(itemStyles);

    const press = useCallback(() => {
      focusState.focused = index;
      onPress?.();
    }, [index, onPress]);

    return (
      <TvPressable onPress={press} style={container}>
        <View style={focused ? focusedContainer : unfocusedContainer} />
        {multiple ? (
          <CheckBox checked={selected} focused={focused} />
        ) : (
          <Radio checked={selected} focused={focused} />
        )}
        <Text style={textStyle}>{title}</Text>
      </TvPressable>
    );
  },
);

type CheckBoxProps = {
  checked?: boolean;
  focused?: boolean;
};

const checkboxStyles = [
  `bg=${AppColors.mainBlue},br=4,bw=1,bc=${AppColors.mainBlue}`,
  'bg=white,br=4,bw=1,bc=white',
  'br=4,bw=1,bc=white',
];

const CheckBox = memo<CheckBoxProps>(({ checked, focused }) => {
  const [checkedStyle, focusedChecked, uncheckedStyle] =
    useGlobalStyles(checkboxStyles);

  return (
    <View
      style={
        checked ? (focused ? focusedChecked : checkedStyle) : uncheckedStyle
      }>
      <AppIcon
        name={AppIcons.check}
        color={
          checked ? (focused ? AppColors.mainBlue : 'white') : 'transparent'
        }
      />
    </View>
  );
});

const radioStyles = [
  'jc,ac,w=24,h=24,bw=1,bc=white,br=150',
  `jc,ac,w=24,h=24,bg=${AppColors.mainBlue},bw=1,bc=transparent,br=150`,
  'jc,ac,w=24,h=24,bg=white,bw=1,bc=transparent,br=150',
  'hide,w=8,h=8,bg=white,br=150',
  'w=8,h=8,bg=white,br=150',
  `w=8,h=8,bg=${AppColors.mainBlue},br=150`,
];

const Radio = memo<CheckBoxProps>(({ checked, focused }) => {
  const [
    uncheckedContainer,
    checkedContainer,
    focusedContainer,
    uncheckedDot,
    checkedDot,
    focusedDot,
  ] = useGlobalStyles(radioStyles);

  const [containerStyle, dotStyle] = useMemo(() => {
    if (checked) {
      if (focused) return [focusedContainer, focusedDot];
      return [checkedContainer, checkedDot];
    }
    return [uncheckedContainer, uncheckedDot];
  }, [checked, focused]);

  return (
    <View style={containerStyle}>
      <View style={dotStyle} />
    </View>
  );
});

type ButtonProps = {
  text: string;
  index: number;
  focusState: FocusStateType;
  onPress?: VoidCb;
};

const buttonStyles = [
  `ac,bg=#393939,mt=10,pv=16,br=16`,
  `fill,bg=${AppColors.mainBlue},br=16`,
  `hide,fill,bg=${AppColors.mainBlue},br=16`,
  'fs=28,fw=500,c=white',
];

const FilterButton = observer<ButtonProps>(
  ({ text, index, focusState, onPress }) => {
    const focused = computed(() => focusState.focused === index).get();

    const [container, focusedContainer, unfocusedContainer, textStyle] =
      useGlobalStyles(buttonStyles);

    const press = useCallback(() => {
      focusState.focused = index;
      onPress?.();
    }, [index, onPress]);

    return (
      <TvPressable onPress={press} style={container}>
        <View style={focused ? focusedContainer : unfocusedContainer} />
        <Text style={textStyle}>{text}</Text>
      </TvPressable>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'flex-end',
  },
});

export default FiltersDialogTv;
